/**
 * InviteMemberModal Component
 *
 * 멤버 초대를 위한 모달 폼
 *
 * 책임:
 * - 멤버 정보 입력 폼 제공
 * - 이메일 유효성 검증
 * - 역할 선택 (Owner/Member)
 */

import { useState } from 'react'
import { Modal } from '@/shared/components/ui/Modal'
import { useToast } from '@/shared/components/ui/Toast'
import type { User, UserRole } from '../types/User'

/**
 * InviteMemberModal Props
 */
export interface InviteMemberModalProps {
  /** 모달 표시 여부 */
  show: boolean
  /** 닫기 핸들러 */
  onClose: () => void
  /** 초대 저장 핸들러 */
  onSave: (input: Omit<User, 'id'>) => Promise<void>
}

/**
 * 멤버 초대 모달
 *
 * 이메일, 이름, 역할을 입력받아 새 멤버를 초대
 */
export function InviteMemberModal({
  show,
  onClose,
  onSave,
}: InviteMemberModalProps) {
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<UserRole>('member')
  const [saving, setSaving] = useState(false)

  /**
   * 이메일 유효성 검증
   */
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  /**
   * 저장 핸들러
   */
  const handleSave = async () => {
    // 유효성 검증
    if (!email.trim()) {
      toast.warning('이메일을 입력해주세요')
      return
    }

    if (!isValidEmail(email.trim())) {
      toast.warning('올바른 이메일 형식이 아닙니다')
      return
    }

    if (!name.trim()) {
      toast.warning('이름을 입력해주세요')
      return
    }

    try {
      setSaving(true)
      await onSave({
        email: email.trim(),
        name: name.trim(),
        role,
        isActive: true,
        joinedAt: new Date().toISOString(),
      })
      // 성공 시 폼 초기화
      setEmail('')
      setName('')
      setRole('member')
    } catch (err) {
      console.error('초대 실패:', err)
    } finally {
      setSaving(false)
    }
  }

  /**
   * 모달 닫기 (폼 초기화)
   */
  const handleClose = () => {
    setEmail('')
    setName('')
    setRole('member')
    onClose()
  }

  return (
    <Modal isOpen={show} onClose={handleClose} title="멤버 초대" size="sm">
      {/* 폼 */}
      <div className="space-y-4">
          {/* 이메일 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              이메일 *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@email.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
            />
          </div>

          {/* 이름 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              이름 *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="홍길동"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
            />
          </div>

          {/* 역할 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              역할 *
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setRole('member')}
                className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                  role === 'member'
                    ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Member
              </button>
              <button
                onClick={() => setRole('owner')}
                className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                  role === 'owner'
                    ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Owner
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Owner는 모든 권한을 가지며, Member는 제한된 권한을 가집니다
            </p>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleClose}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? '초대 중...' : '초대'}
          </button>
        </div>
    </Modal>
  )
}
